import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import sortBy from "lodash/sortBy";
import debounce from "lodash/debounce";
import { AppContext } from "../context/App/appProvider";

/**
 * This hook allows changing the mobile width or desktop width much easier. The desktop and mobile
 * widths are defined as CSS variables and we can modify their values here. This is used on the welcome
 * layout.
 */

type BreakpointDef = {
  desktopWidth: string;
  mobileSizes: number[];
};

export default function useBreakpoints(sizes: BreakpointDef) {
  const sortedMobileSizes = sortBy(sizes.mobileSizes).reverse();
  const { revertGlobalCSSVar, updateGlobalCSSVar } = useContext(AppContext);
  const [windowWidth, setWindowWidth] = useState(0);
  const [visible, setVisible] = useState(false);

  const nextMobileWidth = useMemo(() => {
    if (sortedMobileSizes[1]) {
      return sortedMobileSizes[1];
    }
    return sortedMobileSizes[0];
  }, [sortedMobileSizes]);

  const handleResize = useCallback(() => {
    setWindowWidth(window.innerWidth);
    let breakpointFound = false;
    const sizesFromSmallToLarge = sortedMobileSizes.slice(0).reverse();
    for (let index = 0; index < sizesFromSmallToLarge.length; index += 1) {
      const mobileSize = sizesFromSmallToLarge[index];
      const smallerSize = sizesFromSmallToLarge[index - 1] || sizesFromSmallToLarge[index];
      if (window.innerWidth < mobileSize) {
        updateGlobalCSSVar("mobileBreakpoint", `${mobileSize}px`);
        if (index === 0) {
          revertGlobalCSSVar("mobileWidth");
        } else {
          updateGlobalCSSVar("mobileWidth", `${smallerSize}px`);
        }
        breakpointFound = true;
        return;
      }
    }
    if (!breakpointFound) {
      updateGlobalCSSVar("mobileBreakpoint", `${sortedMobileSizes[0]}px`);
      revertGlobalCSSVar("mobileWidth");
    }
  }, []);
  const handleResizeDebounced = useCallback(debounce(handleResize, 100), []);

  function initLayout() {
    updateGlobalCSSVar("mobileBreakpoint", `${sortedMobileSizes[0]}px`);
    updateGlobalCSSVar("desktopWidth", sizes.desktopWidth);
    updateGlobalCSSVar("mobileWidth", `${nextMobileWidth}px`);

    // We set the page to visible after we have updated the CSS variables so there is no jump in content
    setupWindowResizeListener();
    setTimeout(() => setVisible(true), 10);
  }

  // We call this function before we move to another page to avoid a jump in content
  function revertLayout() {
    revertGlobalCSSVar("mobileBreakpoint");
    revertGlobalCSSVar("desktopWidth");
    window.removeEventListener("resize", handleResizeDebounced);
  }

  function setupWindowResizeListener() {
    window.addEventListener("resize", handleResizeDebounced, {
      passive: true,
    });
    handleResize();
  }

  useEffect(() => {
    initLayout();
    return () => {
      revertLayout();
    };
  }, []);

  return {
    initLayout,
    windowWidth,
    visible,
    revertLayout,
  };
}
